import React from 'react';
import { supabase } from '../lib/supabase';
import { useState, useEffect } from 'react';

function StatusBadge({ ok }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      background: ok ? '#003322' : '#330000',
      color: ok ? '#00cc88' : '#ff4444'
    }}>
      {ok ? '✓ Online' : '✗ Offline'}
    </span>
  );
}

export default function System({ alerts = [] }) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  const railwayOk  = !alerts.some(a => a.msg.includes('Railway'));
  const supabaseOk = !alerts.some(a => a.msg.includes('Supabase'));
  const warnings   = alerts.filter(a => a.type === 'warning');
  const criticals  = alerts.filter(a => a.type === 'critical');

  useEffect(() => {
    async function load() {
      // Total bookings all time
      const { count: totalBookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true });

      // Total cancelled
      const { count: totalCancelled } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'cancelled');

      // Total rescheduled
      const { count: totalRescheduled } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'rescheduled');

      // Bookings missing calendar event ID — shown in detail here
      const { data: missingCal } = await supabase
        .from('bookings')
        .select('booking_id, start_iso, instructor_key, wa_id')
        .eq('status', 'booked')
        .is('gcal_event_id', null)
        .order('created_at', { ascending: false })
        .limit(10);

      // Orphaned students
      const { data: orphaned } = await supabase
        .from('students')
        .select('wa_id, name, created_at')
        .is('instructor_key', null);

      // Incomplete onboarding
      const { data: incomplete } = await supabase
        .from('students')
        .select('wa_id, name, instructor_key')
        .or('name.is.null,has_own_car.is.null');

      setStats({
        totalBookings,
        totalCancelled,
        totalRescheduled,
        missingCal:  missingCal  || [],
        orphaned:    orphaned    || [],
        incomplete:  incomplete  || []
      });
      setLoading(false);
    }
    load();
  }, [alerts]);

  if (loading) return <div style={{ color: '#888', padding: 20 }}>Loading...</div>;

  return (
    <div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
        System
      </div>

      {/* Service status */}
      <div style={{
        background: '#1a1a1a', border: '1px solid #2a2a2a',
        borderRadius: 12, padding: 20, marginBottom: 16
      }}>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Services</div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 0', borderBottom: '1px solid #2a2a2a'
        }}>
          <span style={{ fontSize: 14 }}>Railway API</span>
          <StatusBadge ok={railwayOk} />
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 0'
        }}>
          <span style={{ fontSize: 14 }}>Supabase</span>
          <StatusBadge ok={supabaseOk} />
        </div>
      </div>

      {/* Active alerts */}
      {alerts.length === 0 ? (
        <div style={{
          background: '#0a1a0a', border: '1px solid #00cc88',
          borderRadius: 12, padding: 20, marginBottom: 16,
          color: '#00cc88', fontSize: 14, textAlign: 'center'
        }}>
          ✓ All systems normal — no alerts
        </div>
      ) : (
        <div style={{
          background: '#1a1a1a', border: '1px solid #2a2a2a',
          borderRadius: 12, padding: 20, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
            Active alerts ({alerts.length})
          </div>
          {criticals.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', background: '#2a0000',
              border: '1px solid #ff4444', borderRadius: 8,
              marginBottom: 8, fontSize: 13, color: '#ff6666'
            }}>
              <span>🚨</span><span>{a.msg}</span>
            </div>
          ))}
          {warnings.map((a, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px', background: '#1a1200',
              border: '1px solid #ff8800', borderRadius: 8,
              marginBottom: 8, fontSize: 13, color: '#ffaa44'
            }}>
              <span>⚠️</span><span>{a.msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* All-time booking stats */}
      <div style={{
        background: '#1a1a1a', border: '1px solid #2a2a2a',
        borderRadius: 12, padding: 20, marginBottom: 16
      }}>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>All-time booking stats</div>
        {[
          { label: 'Total bookings',  value: stats.totalBookings },
          { label: 'Cancelled',       value: stats.totalCancelled },
          { label: 'Rescheduled',     value: stats.totalRescheduled }
        ].map(({ label, value }) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '8px 0', borderBottom: '1px solid #2a2a2a', fontSize: 14
          }}>
            <span style={{ color: '#888' }}>{label}</span>
            <span style={{ fontWeight: 600 }}>{value}</span>
          </div>
        ))}
      </div>

      {/* Bookings missing calendar events */}
      {stats.missingCal.length > 0 && (
        <div style={{
          background: '#1a1a1a', border: '1px solid #ff8800',
          borderRadius: 12, padding: 20, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: '#ff8800', marginBottom: 12 }}>
            ⚠️ Bookings missing calendar event ({stats.missingCal.length})
          </div>
          {stats.missingCal.map(b => {
            const time = new Date(b.start_iso).toLocaleDateString('en-GB', {
              timeZone: 'Europe/Dublin', day: '2-digit', month: 'short',
              hour: '2-digit', minute: '2-digit', hour12: false
            });
            return (
              <div key={b.booking_id} style={{
                display: 'flex', justifyContent: 'space-between',
                padding: '6px 0', borderBottom: '1px solid #2a2a2a', fontSize: 13
              }}>
                <span style={{ color: '#888' }}>{b.booking_id}</span>
                <span>{time}</span>
                <span style={{ color: '#666' }}>{b.instructor_key}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Incomplete onboarding */}
      {stats.incomplete.length > 0 && (
        <div style={{
          background: '#1a1a1a', border: '1px solid #ff8800',
          borderRadius: 12, padding: 20, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: '#ff8800', marginBottom: 12 }}>
            ⚠️ Incomplete onboarding ({stats.incomplete.length})
          </div>
          {stats.incomplete.map(s => (
            <div key={s.wa_id} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '6px 0', borderBottom: '1px solid #2a2a2a', fontSize: 13
            }}>
              <span style={{ color: '#888' }}>{s.wa_id}</span>
              <span>{s.name || <span style={{ color: '#ff8800' }}>No name</span>}</span>
              <span style={{ color: '#666' }}>{s.instructor_key}</span>
            </div>
          ))}
        </div>
      )}

      {/* Orphaned students */}
      {stats.orphaned.length > 0 && (
        <div style={{
          background: '#1a1a1a', border: '1px solid #ff4444',
          borderRadius: 12, padding: 20
        }}>
          <div style={{ fontSize: 13, color: '#ff4444', marginBottom: 12 }}>
            ❌ Students with no instructor ({stats.orphaned.length})
          </div>
          {stats.orphaned.map(s => (
            <div key={s.wa_id} style={{
              display: 'flex', justifyContent: 'space-between',
              padding: '6px 0', borderBottom: '1px solid #2a2a2a', fontSize: 13
            }}>
              <span style={{ color: '#888' }}>{s.wa_id}</span>
              <span>{s.name || 'No name'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}